import React, { useEffect, useMemo, useRef } from "react";
import { ExternalLink, Gift } from "lucide-react";
import { api } from "../lib/api";

export default function AdAffiliatePlacements({ items = [], className = "", onContentChange }) {
  const tracked = useRef(new Set());
  const activeItems = useMemo(
    () => {
      const now = Date.now();
      return [...items]
        .filter((item) => {
          const start = item.start_date ? new Date(item.start_date).getTime() : null;
          const end = item.end_date ? new Date(item.end_date).getTime() : null;
          return item.active === true && (item.placement_id || item.id)
            && (!start || start <= now) && (!end || end >= now);
        })
        .sort((first, second) => (first.display_order ?? first.order ?? 0) - (second.display_order ?? second.order ?? 0));
    },
    [items]
  );

  useEffect(() => {
    onContentChange?.(activeItems.length > 0);
  }, [activeItems.length, onContentChange]);

  useEffect(() => {
    activeItems.forEach((item) => {
      const placementId = item.placement_id || item.id;
      if (tracked.current.has(placementId)) return;
      tracked.current.add(placementId);
      Promise.resolve(api.post("/api/ads-affiliate/impression", { placement_id: placementId })).catch(() => {});
    });
  }, [activeItems]);

  if (!activeItems.length) return null;

  const trackClick = (item) => {
    Promise.resolve(api.post("/api/ads-affiliate/click", { placement_id: item.placement_id || item.id })).catch(() => {});
  };

  return (
    <div className={`space-y-3 ${className}`} data-testid="ads-affiliate-placement-list">
      {activeItems.map((item) => {
        const isToy = item.type === "my_toy";
        const imageUrl = item.image_url || item.media_url;
        const image = imageUrl && item.type !== "html_embed";
        const link = item.destination_url;
        const title = item.internal_title || item.title;
        const placementId = item.placement_id || item.id;
        return (
          <article key={placementId} className="min-w-0 overflow-hidden rounded-xl border border-white/15 bg-zinc-950/65 shadow-lg shadow-black/20 backdrop-blur-xl" data-testid={`placement-${placementId}`}>
            {item.type === "html_embed" && item.embed_code && (
              <iframe
                title={title}
                srcDoc={item.embed_code}
                sandbox="allow-scripts allow-popups"
                referrerPolicy="no-referrer"
                className="block h-56 w-full border-0 sm:h-64"
              />
            )}
            {image && (
              <div className="relative max-h-80 overflow-hidden bg-black/40">
                {link ? (
                  <a href={link} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackClick(item)} aria-label={`${item.title}: ${item.button_label || "Learn more"}`}>
                    <img src={imageUrl} alt={title} loading="lazy" className="mx-auto max-h-80 w-full object-contain" />
                  </a>
                ) : (
                  <img src={imageUrl} alt={title} loading="lazy" className="mx-auto max-h-80 w-full object-contain" />
                )}
              </div>
            )}
            {(item.description || link || !image || isToy) && (
              <div className="flex min-w-0 flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                <div className="min-w-0">
                  <h3 className="flex items-center gap-2 break-words text-base font-semibold text-white [overflow-wrap:anywhere]">
                    {isToy && <Gift size={17} className="shrink-0 text-purple-300" aria-hidden="true" />}
                    {title}
                    {item.sponsored && <span className="rounded bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-zinc-300">Sponsored</span>}
                  </h3>
                  {item.description && <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-zinc-300 [overflow-wrap:anywhere]">{item.description}</p>}
                </div>
                {link && (
                  <a href={link} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackClick(item)} className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:border-purple-300/60 hover:bg-purple-950/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-300">
                    {item.button_label || "Learn more"} <ExternalLink size={14} aria-hidden="true" />
                  </a>
                )}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
